<template>
  <div class="card donut-legend-card">
    <div class="card-header">
      <h3 class="card-title">{{ title }}</h3>
    </div>
    <div class="card-body">
      <div class="card-content">
        <div class="donut-chart">
          <canvas ref="chartCanvas"></canvas>
        </div>
        <div class="legend">
          <div class="legend-item" v-for="item in items" :key="item.key">
            <span class="legend-color" :style="{ backgroundColor: item.color }"></span>
            <span class="legend-label">{{ item.label }}</span>
            <span class="legend-value">{{ item.value.toFixed(1) }}%</span>
          </div>
        </div>
      </div>

      <!-- Distribution comparison (optional) -->
      <div v-if="comparisonRows.length > 0" class="comparison">
        <div class="comparison-title">{{ comparisonTitle }}</div>
        <div class="comparison-row" v-for="row in comparisonRows" :key="row.label">
          <span class="comparison-label">{{ row.label }}</span>
          <div class="comparison-bar">
            <div
              v-for="segment in row.items"
              :key="segment.key"
              class="comparison-segment"
              :style="{ width: segment.value + '%', backgroundColor: segment.color }"
              :title="segment.label + ': ' + segment.value.toFixed(1) + '%'"
            ></div>
          </div>
        </div>
        <div class="comparison-axis">
          <span>0</span>
          <span>50</span>
          <span>100</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick, watch } from 'vue';
import { Chart, DoughnutController, ArcElement, Tooltip, Legend } from 'chart.js';
import { colors } from '../../../../consts/colors';

Chart.register(DoughnutController, ArcElement, Tooltip, Legend);

const props = defineProps({
  title: {
    type: String,
    required: true
  },
  // [{ key, label, value (percentage), color }]
  items: {
    type: Array,
    default: () => []
  },
  comparisonTitle: {
    type: String,
    default: ''
  },
  // [{ label, items: [{ key, label, value (percentage), color }] }]
  comparisonRows: {
    type: Array,
    default: () => []
  }
});

const chartCanvas = ref(null);
let chartInstance = null;

const initializeChart = () => {
  if (!chartCanvas.value) return;

  const ctx = chartCanvas.value.getContext('2d');
  chartInstance = new Chart(ctx, {
    type: 'doughnut',
    data: {
      labels: [],
      datasets: [{
        data: [],
        backgroundColor: [],
        borderWidth: 0,
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      cutout: '70%',
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              if (!context.label || context.label === '') {
                return null;
              }
              return context.label + ': ' + context.parsed + '%';
            }
          },
          filter: function(tooltipItem) {
            return tooltipItem.label && tooltipItem.label !== '';
          }
        }
      }
    }
  });
  updateChart();
};

const updateChart = () => {
  if (!chartInstance) {
    nextTick(() => initializeChart());
    return;
  }

  const totalData = props.items.reduce((sum, item) => sum + item.value, 0);

  if (totalData === 0) {
    // Show empty state with light gray circle
    chartInstance.data.labels = [''];
    chartInstance.data.datasets[0].data = [1];
    chartInstance.data.datasets[0].backgroundColor = [colors.gray[300]];
    chartInstance.data.datasets[0].borderColor = [colors.gray[200]];
    chartInstance.data.datasets[0].borderWidth = 2;
  } else {
    chartInstance.data.labels = props.items.map(item => item.label);
    chartInstance.data.datasets[0].data = props.items.map(item => item.value);
    chartInstance.data.datasets[0].backgroundColor = props.items.map(item => item.color);
    chartInstance.data.datasets[0].borderColor = undefined;
    chartInstance.data.datasets[0].borderWidth = 0;
  }

  chartInstance.update();
};

onMounted(() => {
  nextTick(() => initializeChart());
});

watch(() => props.items, () => {
  updateChart();
}, { deep: true });
</script>

<style lang="scss" scoped>
.donut-legend-card {
  background: v-bind('colors.white');
  border: none;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 3px v-bind('colors.shadowColor');
  height: 100%;

  .card-header {
    background-color: v-bind('colors.white');
    border: none;
    padding: 0;
    margin-bottom: 16px;
  }

  .card-title {
    font-size: 18px;
    font-weight: 600;
    color: v-bind('colors.textPrimary');
    margin: 0;
  }

  .card-body {
    padding: 0;
  }

  .card-content {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    align-items: center;

    // Let the grid children shrink below their content size so the
    // canvas and legend never overflow the card on narrow widths
    > * {
      min-width: 0;
    }

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      gap: 24px;
    }
  }

  .donut-chart {
    height: 200px;
    position: relative;

    canvas {
      max-width: 100%;
    }

    @media (max-width: 768px) {
      height: 250px;
      max-width: 250px;
      margin: 0 auto;
    }
  }

  .comparison {
    margin-top: 20px;

    .comparison-title {
      font-size: 12px;
      color: v-bind('colors.gray[500]');
      margin-bottom: 8px;
    }

    .comparison-row {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 6px;
      font-size: 12px;

      .comparison-label {
        flex: 0 0 80px;
        text-align: left;
        color: v-bind('colors.textPrimary');
      }

      .comparison-bar {
        flex: 1;
        display: flex;
        height: 14px;
        border-radius: 2px;
        overflow: hidden;
        background-color: v-bind('colors.gray[100]');

        .comparison-segment {
          height: 100%;
        }
      }
    }

    .comparison-axis {
      display: flex;
      justify-content: space-between;
      margin-left: 88px;
      font-size: 10px;
      color: v-bind('colors.gray[500]');
    }
  }

  .legend {
    overflow: hidden;

    .legend-item {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 6px;
      font-size: 13px;

      .legend-color {
        width: 12px;
        height: 12px;
        border-radius: 2px;
      }

      .legend-label {
        flex: 1;
        color: v-bind('colors.textPrimary');
      }

      .legend-value {
        color: v-bind('colors.gray[500]');
      }
    }
  }
}
</style>
